import React, {useCallback, useState, useEffect} from 'react';
import _ from 'lodash';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {ContentDispatch} from 'framework';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';
import {BOCS} from 'common/requestDetails/constants.js';
import {
  computeBudgetSummaryByNamedFieldPath,
  computeBudgetByInvestments,
} from 'common/requestDetails/utils.js';

// create variable for fundingstreams after filtering ineligible items
let eligibleFS;

const GROUP_BY_FILTERS = {
  byNamedFieldPath: function (props) {
    const fieldPath = _.get(props, 'groupByFieldPath');
    const fieldValueList = _.get(props, 'isTemplatedFieldValueList')
      ? JSON.parse(_.get(props, 'fieldValueList'))
      : _.get(props, 'fieldValueList');
    const fundingStreams = eligibleFS;
    const includeSubProgramFunding = _.get(props, 'includeSubProgramFunding', false);
    if (fieldPath && fieldValueList && fundingStreams) {
      return BudgetUtils.computeBudgetSummaryByNamedFieldPath(
        fieldPath,
        fieldValueList,
        {
          children: fundingStreams,
        },
        includeSubProgramFunding
      );
    }
  },

  byNamedFieldPathWithConditionalCheck: function (props) {
    let additionalCheckPath = _.get(props, 'additionalCheckPath');
    const fieldPath = _.get(props, 'groupByFieldPath');
    const fieldValueList = _.get(props, 'fieldValueList');
    const fundingStreams = eligibleFS;
    const includeSubProgramFunding = _.get(props, 'includeSubProgramFunding', false);
    if (fieldPath && fieldValueList && fundingStreams) {
      return computeBudgetSummaryByNamedFieldPath(
        fieldPath,
        fieldValueList,
        {
          children: fundingStreams,
        },
        includeSubProgramFunding,
        additionalCheckPath
      );
    }
  },
  byCostItemFieldForConstruction: function (props) {
    const ancestorItemCode = _.get(props, 'ancestorItemCode');
    const costItemFieldPath = _.get(props, 'costItemFieldPath');
    const costItemFieldValueToFieldNameMap = _.get(
      props,
      'costItemFieldValueToFieldNameMap'
    );
    const fundingStreams = eligibleFS;
    if (
      ancestorItemCode &&
      costItemFieldPath &&
      costItemFieldValueToFieldNameMap &&
      fundingStreams
    ) {
      return BudgetUtils.computeConstructionBudgetSummaryByCostItemField(
        ancestorItemCode,
        costItemFieldPath,
        costItemFieldValueToFieldNameMap,
        {children: fundingStreams}
      );
    }
  },
  byCostItemFieldForConstructionNoAncestor: function (props) {
    const costItemFieldPath = _.get(props, 'costItemFieldPath');
    const costItemFieldValueToFieldNameMap = _.get(
      props,
      'costItemFieldValueToFieldNameMap'
    );
    const fundingStreams = eligibleFS;
    if (costItemFieldPath && costItemFieldValueToFieldNameMap && fundingStreams) {
      return BudgetUtils.computeConstructionBudgetSummaryByCostItemFieldNoAncestor(
        costItemFieldPath,
        costItemFieldValueToFieldNameMap,
        {children: fundingStreams}
      );
    }
  },
  byItemCode: function (data) {
    if (_.get(data, 'itemCodes') && _.get(data, 'fundingStreams')) {
      return BudgetUtils.computeBudgetByItemCode(
        data.itemCodes,
        {children: eligibleFS},
        _.get(data, 'budget.nonFederal.constructionProgramIncome') || 0
      );
    }
  },
  byInvestments: function (data) {
    if (_.get(data, 'fundingStreams')) {
      return BudgetUtils.computeBudgetByInvestments(
        {children: eligibleFS},
        _.get(data, 'orgId')
      );
    }
  },
  byInvestmentsHSGPNSGP: function (data) {
    if (_.get(data, 'fundingStreams')) {
      return computeBudgetByInvestments({children: eligibleFS}, _.get(data, 'orgId'));
    }
  },
  byBOC: function (data) {
    if (_.get(data, 'fundingStreams') && _.get(data, 'numPeriods')) {
      return {
        budgetSummary: BudgetUtils.computeBudgetSummary(
          _.get(data, 'bocs') || [...BOCS],
          eligibleFS,
          data.numPeriods,
          (_.get(data, 'constructionProgramIncomeProperty') &&
            _.get(data, _.get(data, 'constructionProgramIncomeProperty'))) ||
            0,
          _.get(data, 'managementCostItemCodes') || []
        ),
      };
    }
  },
};

const isIneligible = (fsItem) => {
  const result = _.get(fsItem, 'additionalInformation.ineligible');
  return result;
};

const filterEligibleChildren = (fundingStreams) => {
  if (typeof fundingStreams !== 'object') {
    return fundingStreams;
  }

  const filteredChildren = {};

  for (const [key, childvalue] of Object.entries(fundingStreams)) {
    if (isIneligible(childvalue)) {
      continue;
    }

    if (typeof childvalue === 'object') {
      filteredChildren[key] = processItemRecursively(childvalue);
    } else {
      filteredChildren[key] = childvalue;
    }
  }

  return filteredChildren;
};

const processItemRecursively = (item) => {
  if (item.children) {
    item.children = filterEligibleChildren(item.children);
  }

  return item;
};

export const BudgetSummaryListener = (props) => {
  const {
    contents,
    entityDataPath,
    entityType,
    fieldPath,
    groupBy,
    saveEntityData,
  } = props;
  const [done, setDone] = useState(false);
  const keys = Object.keys(_.get(props.dependencies, '0'));
  const [budgetSummary, setBudgetSummary] = useState();
  const args = [];
  _.each(keys, (key) => args.push(props[key]));

  useEffect(() => {
    if (props.fundingStreams) {
      //cloning fundings streams to be able to filter out eligible items
      let filteredFS = _.cloneDeep(props.fundingStreams);
      eligibleFS = filterEligibleChildren(filteredFS);

      const budgetSummaryData = GROUP_BY_FILTERS[groupBy](props);
      if (
        !_.isEmpty(budgetSummaryData) &&
        !_.isEqual(budgetSummaryData.budgetSummary, budgetSummary)
      ) {
        setBudgetSummary(budgetSummaryData.budgetSummary);
      }
    }
  }, [...args]);
  const saveData = useCallback(async () => {
    if (budgetSummary && !_.isEmpty(budgetSummary)) {
      saveEntityData(entityDataPath, entityType, budgetSummary, fieldPath);
      setDone(true);
    }
  }, [budgetSummary]);
  useEffect(() => {
    saveData();
  }, [budgetSummary, saveData]);

  return (
    <>
      {done
        ? _.map(contents, (entry, index) => {
            return <ContentDispatch config={entry} key={entry.title} />;
          })
        : null}
    </>
  );
};

export default connectDynamic(BudgetSummaryListener);
